
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import NavigationWrapper from "./NavigationWrapper";
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin, Clock, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import DynamicBreadcrumb from "./navigation/DynamicBreadcrumb";

type LayoutProps = {
  children: ReactNode;
};

const Layout = ({ children }: LayoutProps) => {
  const currentYear = 2025;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-teal-800 to-blue-900 flex flex-col overflow-x-hidden">
      <NavigationWrapper />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <DynamicBreadcrumb />
      </div>
      <main className="flex-grow">
        {children}
      </main>
      <footer className="relative mt-20 overflow-hidden">
        {/* Sophisticated Background */}
        <div className="absolute inset-0">
          {/* Gradient mesh background */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/80 via-teal-900/90 to-blue-950/80" />
          
          {/* Floating orbs for depth */}
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-gradient-to-br from-emerald-500/8 to-teal-500/4 rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-gradient-to-br from-blue-500/8 to-purple-500/4 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-gradient-to-br from-teal-500/6 to-emerald-500/3 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          
          {/* Subtle dot pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
              backgroundSize: '24px 24px'
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-16">
          
          {/* Hero section with brand */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <div className="relative">
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-blue-500 flex items-center justify-center shadow-2xl border border-white/30">
                  <span className="text-white font-light text-3xl">F</span>
                </div>
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-blue-500/30 blur-lg -z-10" />
              </div>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-light text-white mb-4 tracking-wide">
              Florida Highwaymen Auction
            </h2>
            <p className="text-white/60 text-lg font-light max-w-2xl mx-auto leading-relaxed">
              Preserving and celebrating the artistic legacy of Florida's most influential landscape painters through authentic auctions and expert curation.
            </p>
          </div>

          {/* Main content grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-16">
            
            {/* Company Info */}
            <div className="space-y-6">
              <h3 className="text-xl font-light text-white mb-6 relative">
                About Our Gallery
                <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full" />
              </h3>
              <p className="text-white/70 font-light leading-relaxed text-sm">
                Specializing in the acquisition, authentication, and auction of original Florida Highwaymen artworks with over two decades of expertise in this unique American art movement.
              </p>
              
              {/* Social links with enhanced design */}
              <div className="flex space-x-4 pt-4">
                <a href="https://facebook.com" 
                   className="group relative p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-blue-400/50 transition-all duration-300 hover:scale-110"
                   aria-label="Facebook">
                  <Facebook className="h-5 w-5 text-white/60 group-hover:text-blue-400 transition-colors duration-300" />
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </a>
                <a href="https://instagram.com" 
                   className="group relative p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-pink-400/50 transition-all duration-300 hover:scale-110"
                   aria-label="Instagram">
                  <Instagram className="h-5 w-5 text-white/60 group-hover:text-pink-400 transition-colors duration-300" />
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </a>
                <a href="https://twitter.com" 
                   className="group relative p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-sky-400/50 transition-all duration-300 hover:scale-110"
                   aria-label="Twitter">
                  <Twitter className="h-5 w-5 text-white/60 group-hover:text-sky-400 transition-colors duration-300" />
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-sky-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </a>
              </div>
            </div>

            {/* Quick Navigation */}
            <div className="space-y-6">
              <h3 className="text-xl font-light text-white mb-6 relative">
                Explore
                <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full" />
              </h3>
              <nav className="space-y-3">
                <Link to="/" className="group flex items-center text-white/70 hover:text-emerald-400 transition-all duration-300 font-light text-sm">
                  <div className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-emerald-400 mr-3 transition-colors duration-300" />
                  Home
                </Link>
                <Link to="/florida-highwaymen" className="group flex items-center text-white/70 hover:text-emerald-400 transition-all duration-300 font-light text-sm">
                  <div className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-emerald-400 mr-3 transition-colors duration-300" />
                  Florida Highwaymen Guide
                </Link>
                <Link to="/auctions" className="group flex items-center text-white/70 hover:text-emerald-400 transition-all duration-300 font-light text-sm">
                  <div className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-emerald-400 mr-3 transition-colors duration-300" />
                  Live Auctions
                </Link>
                <Link to="/artworks" className="group flex items-center text-white/70 hover:text-emerald-400 transition-all duration-300 font-light text-sm">
                  <div className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-emerald-400 mr-3 transition-colors duration-300" />
                  Gallery Collection
                </Link>
                <Link to="/collecting-florida-highwaymen-art" className="group flex items-center text-white/70 hover:text-emerald-400 transition-all duration-300 font-light text-sm">
                  <div className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-emerald-400 mr-3 transition-colors duration-300" />
                  Collecting Guide
                </Link>
                <Link to="/florida-highwaymen-appraisal" className="group flex items-center text-white/70 hover:text-emerald-400 transition-all duration-300 font-light text-sm">
                  <div className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-emerald-400 mr-3 transition-colors duration-300" />
                  Appraisal Guide
                </Link>
                <Link to="/broadcast" className="group flex items-center text-white/70 hover:text-emerald-400 transition-all duration-300 font-light text-sm">
                  <div className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-emerald-400 mr-3 transition-colors duration-300" />
                  Live Broadcasts
                </Link>
                <Link to="/reviews" className="group flex items-center text-white/70 hover:text-emerald-400 transition-all duration-300 font-light text-sm">
                  <div className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-emerald-400 mr-3 transition-colors duration-300" />
                  Reviews & Testimonials
                </Link>
                <Link to="/consign" className="group flex items-center text-white/70 hover:text-emerald-400 transition-all duration-300 font-light text-sm">
                  <div className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-emerald-400 mr-3 transition-colors duration-300" />
                  Consign Artwork
                </Link>
                <Link to="/appraisal" className="group flex items-center text-white/70 hover:text-emerald-400 transition-all duration-300 font-light text-sm">
                  <div className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-emerald-400 mr-3 transition-colors duration-300" />
                  Art Appraisal
                </Link>
              </nav>
            </div>

            {/* Contact Information */}
            <div className="space-y-6" itemScope itemType="https://schema.org/LocalBusiness">
              <h3 className="text-xl font-light text-white mb-6 relative">
                Get in Touch
                <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full" />
              </h3>
              <meta itemProp="name" content="Florida Highwaymen Auction" />
              <div className="space-y-4">
                <div className="group flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors duration-300" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                  <MapPin className="h-5 w-5 mt-0.5 text-emerald-400 flex-shrink-0" />
                  <div>
                    <p className="text-white/90 font-medium text-sm">Gallery Location</p>
                    <p className="text-white/60 text-sm font-light">
                      <span itemProp="streetAddress">8214 Griffin Rd</span>,
                      <span> </span>
                      <span itemProp="addressLocality">Davie</span>,
                      <span> </span>
                      <span itemProp="addressRegion">FL</span>
                      <span> </span>
                      <span itemProp="postalCode">33328</span>
                    </p>
                    <meta itemProp="addressCountry" content="US" />
                  </div>
                </div>
                
                <a href="tel:+19546050748" className="group flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors duration-300" itemProp="telephone">
                  <Phone className="h-5 w-5 text-blue-400" />
                  <div>
                    <p className="text-white/90 font-medium text-sm">Call Us</p>
                    <p className="text-white/60 text-sm font-light group-hover:text-emerald-400 transition-colors duration-300">+1 (954) 605-0748</p>
                  </div>
                </a>
                
                <a href="mailto:info@floridahighwaymenauction.com" className="group flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors duration-300" itemProp="email">
                  <Mail className="h-5 w-5 text-teal-400" />
                  <div>
                    <p className="text-white/90 font-medium text-sm">Email Us</p>
                    <p className="text-white/60 text-sm font-light group-hover:text-emerald-400 transition-colors duration-300">info@floridahighwaymenauction.com</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Newsletter & Hours */}
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-light text-white mb-6 relative">
                  Stay Connected
                  <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full" />
                </h3>
                <p className="text-white/60 mb-4 font-light text-sm leading-relaxed">
                  Get exclusive updates on new acquisitions, upcoming auctions, and special events.
                </p>
                <form className="space-y-3" onSubmit={(e) => e.preventDefault()}>
                  <div className="relative">
                    <input 
                      type="email"
                      placeholder="Enter your email"
                      className="w-full px-4 py-3 rounded-xl border border-white/20 bg-white/5 backdrop-blur-xl text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:border-emerald-400/50 font-light text-sm transition-all duration-300"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-medium text-sm tracking-wide border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl hover:scale-[1.02] py-3">
                    Subscribe Now
                  </Button>
                </form>
              </div>
              
              {/* Business Hours */}
              <div className="p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
                <h4 className="text-white font-medium mb-3 flex items-center text-sm">
                  <Clock className="h-4 w-4 mr-2 text-emerald-400" />
                  Gallery Hours
                </h4>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between text-white/70">
                    <span>Monday - Friday</span>
                    <span>10:30 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between text-white/50">
                    <span>Weekend</span>
                    <span>By Appointment</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom section */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="text-center md:text-left">
                <div className="text-white/60 font-light text-xs mb-2">
                  Auctioneer License # AU5482 • Auctioneer House License # AU5482
                </div>
                <p className="text-white/50 font-light text-sm">
                  © {currentYear} Florida Highwaymen Auction LLC. All rights reserved.
                </p>
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <Link to="/privacy" className="text-white/50 hover:text-emerald-400 transition-colors duration-300 font-light">Privacy</Link>
                <Link to="/terms" className="text-white/50 hover:text-teal-400 transition-colors duration-300 font-light">Terms</Link>
                <Link to="/cookies" className="text-white/50 hover:text-blue-400 transition-colors duration-300 font-light">Cookies</Link>
                <Link to="/editorial-policy" className="text-white/50 hover:text-emerald-400 transition-colors duration-300 font-light">Editorial Policy</Link>
              </div>
            </div>
          </div>
        </div>

        {/* Powered By Vaynerov Technologies - Sophisticated Black Section */}
        <div className="relative overflow-hidden border-t border-white/5">
          {/* Elegant Dark Background */}
          <div className="absolute inset-0">
            {/* Base gradient - deep blacks */}
            <div className="absolute inset-0 bg-gradient-to-br from-black via-slate-950 to-black" />
            
            {/* Subtle animated orbs for depth */}
            <div className="absolute -left-24 top-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-slate-800/10 to-slate-700/5 rounded-full blur-3xl" />
            <div className="absolute -right-24 top-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-slate-800/10 to-slate-700/5 rounded-full blur-3xl" />
            
            {/* Wave Animations - Full Width Corner to Corner */}
            <div className="absolute bottom-0 left-0 right-0 overflow-visible h-32 w-full">
              {/* Wave 1 - Slate */}
              <svg 
                className="absolute bottom-0 left-0 h-32 opacity-20"
                style={{ 
                  width: '200%',
                  minWidth: '100%',
                  animation: 'wave 20s ease-in-out infinite' 
                }}
                viewBox="0 0 2400 120"
                preserveAspectRatio="none"
              >
                <path 
                  d="M0,50 Q300,20 600,50 T1200,50 T1800,50 T2400,50 L2400,120 L0,120 Z" 
                  fill="url(#wave-gradient-1)"
                />
                <defs>
                  <linearGradient id="wave-gradient-1" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#64748b" stopOpacity="0.3" />
                    <stop offset="50%" stopColor="#94a3b8" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#64748b" stopOpacity="0.3" />
                  </linearGradient>
                </defs>
              </svg>
              
              {/* Wave 2 - Blue tint */}
              <svg 
                className="absolute bottom-0 left-0 h-32 opacity-15"
                style={{ 
                  width: '200%',
                  minWidth: '100%',
                  animation: 'wave 15s ease-in-out infinite reverse',
                  animationDelay: '-5s'
                }}
                viewBox="0 0 2400 120"
                preserveAspectRatio="none"
              >
                <path 
                  d="M0,60 Q300,30 600,60 T1200,60 T1800,60 T2400,60 L2400,120 L0,120 Z" 
                  fill="url(#wave-gradient-2)"
                />
                <defs>
                  <linearGradient id="wave-gradient-2" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#475569" stopOpacity="0.3" />
                    <stop offset="50%" stopColor="#64748b" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#475569" stopOpacity="0.3" />
                  </linearGradient>
                </defs>
              </svg>
              
              {/* Wave 3 - Lighter */}
              <svg 
                className="absolute bottom-0 left-0 h-32 opacity-10"
                style={{ 
                  width: '200%',
                  minWidth: '100%',
                  animation: 'wave 25s ease-in-out infinite',
                  animationDelay: '-10s'
                }}
                viewBox="0 0 2400 120"
                preserveAspectRatio="none"
              >
                <path 
                  d="M0,40 Q300,10 600,40 T1200,40 T1800,40 T2400,40 L2400,120 L0,120 Z" 
                  fill="url(#wave-gradient-3)"
                />
                <defs>
                  <linearGradient id="wave-gradient-3" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#cbd5e1" stopOpacity="0.2" />
                    <stop offset="50%" stopColor="#e2e8f0" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.2" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            
            {/* Subtle grid pattern */}
            <div 
              className="absolute inset-0 opacity-[0.015]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundSize: '60px 60px'
              }}
            />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              {/* Elegant Logo with Border-Following Glow */}
              <a 
                href="https://www.instagram.com/vaynerov/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group relative cursor-pointer"
                aria-label="Visit Vaynerov Technologies on Instagram"
              >
                <div className="relative flex items-center justify-center w-20 h-20 p-2">
                  {/* Main bright white glow - POWERFUL */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 80 80" preserveAspectRatio="xMidYMid meet" style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.8))', overflow: 'visible' }}>
                    <defs>
                      <linearGradient id="borderGlowMain" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="white" stopOpacity="0" />
                        <stop offset="20%" stopColor="white" stopOpacity="0.5" />
                        <stop offset="40%" stopColor="white" stopOpacity="0.9" />
                        <stop offset="50%" stopColor="white" stopOpacity="1" />
                        <stop offset="60%" stopColor="white" stopOpacity="0.9" />
                        <stop offset="80%" stopColor="white" stopOpacity="0.5" />
                        <stop offset="100%" stopColor="white" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    
                    <rect 
                      x="10" 
                      y="10" 
                      width="60" 
                      height="60" 
                      rx="12" 
                      ry="12"
                      fill="none"
                      stroke="url(#borderGlowMain)"
                      strokeWidth="4"
                      strokeDasharray="60 240"
                      strokeDashoffset="0"
                      className="animate-border-travel"
                    />
                  </svg>
                  
                  {/* Trail 1 - Slate blue */}
                  <svg className="absolute inset-0 w-full h-full opacity-70" viewBox="0 0 80 80" preserveAspectRatio="xMidYMid meet" style={{ filter: 'blur(2px)', overflow: 'visible' }}>
                    <defs>
                      <linearGradient id="trailGlow1" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#94a3b8" stopOpacity="0" />
                        <stop offset="30%" stopColor="#cbd5e1" stopOpacity="0.6" />
                        <stop offset="50%" stopColor="#e2e8f0" stopOpacity="0.8" />
                        <stop offset="70%" stopColor="#cbd5e1" stopOpacity="0.6" />
                        <stop offset="100%" stopColor="#94a3b8" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    
                    <rect 
                      x="10" 
                      y="10" 
                      width="60" 
                      height="60" 
                      rx="12" 
                      ry="12"
                      fill="none"
                      stroke="url(#trailGlow1)"
                      strokeWidth="3"
                      strokeDasharray="50 250"
                      strokeDashoffset="-30"
                      className="animate-border-travel"
                    />
                  </svg>
                  
                  {/* Trail 2 - Deeper slate */}
                  <svg className="absolute inset-0 w-full h-full opacity-50" viewBox="0 0 80 80" preserveAspectRatio="xMidYMid meet" style={{ filter: 'blur(3px)', overflow: 'visible' }}>
                    <defs>
                      <linearGradient id="trailGlow2" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#64748b" stopOpacity="0" />
                        <stop offset="40%" stopColor="#94a3b8" stopOpacity="0.7" />
                        <stop offset="60%" stopColor="#94a3b8" stopOpacity="0.7" />
                        <stop offset="100%" stopColor="#64748b" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    
                    <rect 
                      x="10" 
                      y="10" 
                      width="60" 
                      height="60" 
                      rx="12" 
                      ry="12"
                      fill="none"
                      stroke="url(#trailGlow2)"
                      strokeWidth="3"
                      strokeDasharray="45 255"
                      strokeDashoffset="-60"
                      className="animate-border-travel"
                    />
                  </svg>
                  
                  {/* Trail 3 - Darkest slate */}
                  <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 80 80" preserveAspectRatio="xMidYMid meet" style={{ filter: 'blur(4px)', overflow: 'visible' }}>
                    <defs>
                      <linearGradient id="trailGlow3" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#475569" stopOpacity="0" />
                        <stop offset="40%" stopColor="#64748b" stopOpacity="0.6" />
                        <stop offset="60%" stopColor="#64748b" stopOpacity="0.6" />
                        <stop offset="100%" stopColor="#475569" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    
                    <rect 
                      x="10" 
                      y="10" 
                      width="60" 
                      height="60" 
                      rx="12" 
                      ry="12"
                      fill="none"
                      stroke="url(#trailGlow3)"
                      strokeWidth="3"
                      strokeDasharray="40 260"
                      strokeDashoffset="-90"
                      className="animate-border-travel"
                    />
                  </svg>
                  
                  {/* Ambient outer glow */}
                  <div className="absolute inset-2 bg-gradient-to-br from-slate-400/10 to-slate-600/10 rounded-2xl blur-xl" />
                  
                  {/* Logo container */}
                  <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 flex items-center justify-center shadow-2xl border border-white/20 group-hover:border-white/30 group-hover:scale-105 transition-all duration-700 overflow-visible p-1">
                    <img 
                      src="https://opibkpaosvqkgtrhvklb.supabase.co/storage/v1/object/public/images/476372859_814071340882711_4105204900190883767_n.jpg"
                      alt="Vaynerov Technologies Logo"
                      className="w-full h-full object-cover rounded-xl group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                </div>
              </a>

              {/* Text Content */}
              <a 
                href="https://www.instagram.com/vaynerov/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-center md:text-left group cursor-pointer"
              >
                <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                  <span className="text-white/30 font-light text-xs tracking-[0.2em] uppercase">Powered By</span>
                  <div className="h-px w-12 bg-gradient-to-r from-white/20 via-white/10 to-transparent" />
                </div>
                
                <h3 className="relative inline-block mb-1">
                  <span className="text-2xl md:text-3xl font-light tracking-wide text-white group-hover:text-white/90 transition-colors duration-500">
                    Vaynerov Technologies
                  </span>
                  
                  {/* Minimal underline */}
                  <div className="absolute -bottom-0.5 left-0 w-0 h-px bg-gradient-to-r from-white/40 to-transparent group-hover:w-full transition-all duration-700" />
                </h3>
                
                <p className="text-white/20 font-light text-xs tracking-wide group-hover:text-white/30 transition-colors duration-500">
                  Innovative Web Solutions & Digital Excellence
                </p>
              </a>
            </div>
          </div>

          {/* Bottom accent line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>
      </footer>

      {/* Add keyframes for animations */}
      <style>{`
        @keyframes wave {
          0%, 100% {
            transform: translateX(0%) translateY(0%);
          }
          50% {
            transform: translateX(-50%) translateY(5%);
          }
        }
        
        @keyframes border-travel {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: -300;
          }
        }
        
        @keyframes border-travel-slow {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: -320;
          }
        }
        
        .animate-border-travel {
          animation: border-travel 3s linear infinite;
        }
        
        .animate-border-travel-slow {
          animation: border-travel-slow 4s linear infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default Layout;
